import { theme } from '@Guidelines/Theme/theme';
import TitleEditor from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/EditableTitle/TitleEditor';
import editedTitleSignal, {
	setEditedTitle,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/EditableTitle/Signals/editedTitleSignal';
import { css } from '@emotion/react';
import { Pencil } from 'lucide-react';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { Tooltip, useOnUnmount } from '@verclam/ui-kit';

const titleButtonStyle = css`
	display: inline-flex;
	align-items: center;
	gap: 0.375rem;
	padding: 0.125rem 0.375rem;
	margin: -0.125rem -0.375rem;
	font: inherit;
	color: inherit;
	text-align: left;
	background: transparent;
	border: none;
	border-radius: ${theme.radii.xsmall};

	svg {
		flex-shrink: 0;
		color: ${theme.colors.inkFaint};
	}

	&:hover,
	&:focus-visible {
		background: ${theme.colors.surfaceMuted};
		cursor: pointer;

		svg {
			color: ${theme.colors.inkMuted};
		}
	}

	&:focus-visible {
		outline: 1.5px solid ${theme.colors.accent};
	}
`;

type Props = {
	readonly title: string;
	/** Identifies this title in `editedTitleSignal`: one title is edited at a time. */
	readonly editKey: string;
	readonly canRename: boolean;
	/** Hidden label of the input. */
	readonly fieldLabel: string;
	/** Resolves on success; rejects with the `ExtractedMessages` of the failure action. */
	readonly onRename: (name: string) => Promise<void>;
};

/**
 * A title that renames in place: plain text without `canRename`, otherwise a button (pencil, « Renommer »
 * tooltip) that swaps for `TitleEditor`. The button keeps the title as its accessible name.
 */
const EditableTitle: React.FC<Props> = ({ title, editKey, canRename, fieldLabel, onRename }) => {
	const editedKey = useSignalValue(editedTitleSignal);

	const handleEdit = React.useCallback(() => {
		setEditedTitle(editKey);
	}, [editKey]);

	useOnUnmount(() => {
		if (editedTitleSignal.peek() !== editKey) {
			return;
		}

		setEditedTitle(null);
	});

	if (!canRename) {
		return <React.Fragment>{title}</React.Fragment>;
	}

	if (editedKey === editKey) {
		return <TitleEditor title={title} fieldLabel={fieldLabel} onRename={onRename} />;
	}

	return (
		<Tooltip content="Renommer" position="top" event="both">
			<button type="button" css={titleButtonStyle} onClick={handleEdit}>
				{title}
				<Pencil size={14} aria-hidden="true" />
			</button>
		</Tooltip>
	);
};

export default EditableTitle;
